// ============================================================================
// USE SECOND SCREEN BROADCAST - Publisher Hook
// Packages LiveSession state and broadcasts via BroadcastChannel to popup
// ============================================================================

import { useEffect, useRef, useCallback } from 'react';
import type {
  SecondScreenState,
  SecondScreenMessage,
  SecondScreenSnapshot,
  SecondScreenPatch,
  SecondScreenPing,
  SecondScreenMindBubble,
  SecondScreenCinematicCard,
  SecondScreenCinematicContribution,
  SecondScreenTaskUpdate,
  SecondScreenRequirementsFocus,
  CinematicCard,
  CinematicContribution,
  PinnedContentItem,
} from '../types/second-screen.types';
import type { AvatarSuggestion } from '../services/workshop';
import type { SessionTaskSummary } from '../types/session-task.types';

const SNAPSHOT_INTERVAL_MS = 30_000;
const DEBOUNCE_MS = 1_000;
const FAST_DEBOUNCE_MS = 500;

export function useSecondScreenBroadcast(
  sessionId: string,
  state: Omit<SecondScreenState, 'sessionId'>
): {
  sendSpotlight: (contribution: AvatarSuggestion, targetScreen?: number) => void;
  clearSpotlight: (targetScreen?: number) => void;
  pinContent: (item: PinnedContentItem, targetScreen?: number) => void;
  unpinContent: (itemId: string, targetScreen?: number) => void;
  sendMindBubble: (avatarRole: string, avatarName: string, thought: string) => void;
  sendCinematicCard: (card: CinematicCard) => void;
  sendCinematicContribution: (contribution: CinematicContribution) => void;
  sendTaskUpdate: (tasks: SessionTaskSummary[]) => void;
  sendRequirementsFocus: (activePersonaId: string | null, storiesScrollTop: number) => void;
} {
  const channelRef = useRef<BroadcastChannel | null>(null);
  const seqRef = useRef(0);
  const stateRef = useRef(state);
  const prevStateRef = useRef<Omit<SecondScreenState, 'sessionId'> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fastDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snapshotIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingSnapshotRequestRef = useRef(false);

  // Always keep ref in sync so snapshot handler reads latest state
  stateRef.current = state;

  const nextSeq = useCallback(() => ++seqRef.current, []);

  const sendMessage = useCallback((msg: SecondScreenMessage) => {
    channelRef.current?.postMessage(msg);
  }, []);

  const sendSnapshot = useCallback(() => {
    const msg: SecondScreenSnapshot = {
      type: 'snapshot',
      sessionId,
      seq: nextSeq(),
      ts: Date.now(),
      payload: { sessionId, ...stateRef.current },
    };
    sendMessage(msg);
  }, [sessionId, nextSeq, sendMessage]);

  // Initialize channel
  useEffect(() => {
    const channel = new BroadcastChannel(`spectrum-second-screen-${sessionId}`);
    channelRef.current = channel;
    pendingSnapshotRequestRef.current = false;

    channel.onmessage = (event: MessageEvent<SecondScreenMessage>) => {
      if (event.data.type === 'request_snapshot') {
        const s = stateRef.current;
        const ready = (s.phases?.length ?? 0) > 0 || (s.sessionAvatars?.length ?? 0) > 0;
        if (ready) {
          sendSnapshot();
        } else {
          pendingSnapshotRequestRef.current = true;
        }
      }
    };

    // Periodic snapshot heartbeat
    snapshotIntervalRef.current = setInterval(() => {
      sendSnapshot();
    }, SNAPSHOT_INTERVAL_MS);

    // Initial ping
    const ping: SecondScreenPing = {
      type: 'ping',
      sessionId,
      seq: nextSeq(),
      ts: Date.now(),
    };
    sendMessage(ping);

    return () => {
      channel.close();
      channelRef.current = null;
      if (snapshotIntervalRef.current) clearInterval(snapshotIntervalRef.current);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (fastDebounceTimerRef.current) clearTimeout(fastDebounceTimerRef.current);
    };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Immediate fields: speakingAvatar, currentPhaseIndex
  useEffect(() => {
    if (!channelRef.current) return;
    const prev = prevStateRef.current;
    if (!prev) return;

    const immediatePatch: Partial<SecondScreenState> = {};
    let hasChanges = false;

    if (state.speakingAvatar !== prev.speakingAvatar) {
      immediatePatch.speakingAvatar = state.speakingAvatar;
      hasChanges = true;
    }
    if (state.currentPhaseIndex !== prev.currentPhaseIndex) {
      immediatePatch.currentPhaseIndex = state.currentPhaseIndex;
      immediatePatch.phases = state.phases;
      hasChanges = true;
    }
    if (state.isAvatarsThinking !== prev.isAvatarsThinking) {
      immediatePatch.isAvatarsThinking = state.isAvatarsThinking;
      hasChanges = true;
    }

    if (hasChanges) {
      const msg: SecondScreenPatch = {
        type: 'patch',
        sessionId,
        seq: nextSeq(),
        ts: Date.now(),
        fields: immediatePatch,
      };
      sendMessage(msg);
      // Update prev ref so subsequent effects see the change
      prevStateRef.current = { ...prevStateRef.current!, ...immediatePatch };
    }
  }, [state.speakingAvatar, state.currentPhaseIndex, state.isAvatarsThinking, sessionId, nextSeq, sendMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fast debounce fields: transcript, pendingContributions
  useEffect(() => {
    if (!channelRef.current) return;
    const prev = prevStateRef.current;
    if (!prev) return;

    // Capture current values for the debounced closure
    const currentTranscript = state.transcript;
    const currentPendingContributions = state.pendingContributions;

    if (fastDebounceTimerRef.current) clearTimeout(fastDebounceTimerRef.current);

    fastDebounceTimerRef.current = setTimeout(() => {
      const fastPatch: Partial<SecondScreenState> = {};
      let hasChanges = false;

      if (currentTranscript !== prevStateRef.current?.transcript) {
        fastPatch.transcript = currentTranscript;
        hasChanges = true;
      }
      if (currentPendingContributions !== prevStateRef.current?.pendingContributions) {
        fastPatch.pendingContributions = currentPendingContributions;
        hasChanges = true;
      }

      if (hasChanges) {
        const msg: SecondScreenPatch = {
          type: 'patch',
          sessionId,
          seq: nextSeq(),
          ts: Date.now(),
          fields: fastPatch,
        };
        sendMessage(msg);
        prevStateRef.current = { ...prevStateRef.current!, ...fastPatch };
      }
    }, FAST_DEBOUNCE_MS);

    return () => {
      if (fastDebounceTimerRef.current) clearTimeout(fastDebounceTimerRef.current);
    };
  }, [state.transcript, state.pendingContributions, sessionId, nextSeq, sendMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Normal debounce: everything else
  useEffect(() => {
    if (!channelRef.current) return;
    const prev = prevStateRef.current;
    if (!prev) {
      prevStateRef.current = state;
      return;
    }

    // Capture current slow field values for the debounced closure
    const currentState = state;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      const patch: Partial<SecondScreenState> = {};
      let hasChanges = false;

      const slowFields: (keyof Omit<SecondScreenState, 'sessionId'>)[] = [
        'sessionName', 'backgroundInfo', 'phases', 'sessionAvatars',
        'requirements', 'facilitatorGuidance', 'sessionDevices',
        'attendees', 'facilitators', 'sessionStatus', 'activeTasks', 'governanceData',
        'sessionCreatedAt', 'inspirationCards', 'graphData', 'pinnedContent',
        'doubleDiamondProgress',
      ];

      for (const key of slowFields) {
        if ((currentState as any)[key] !== (prevStateRef.current as any)?.[key]) {
          (patch as any)[key] = (currentState as any)[key];
          hasChanges = true;
        }
      }

      if (hasChanges) {
        const msg: SecondScreenPatch = {
          type: 'patch',
          sessionId,
          seq: nextSeq(),
          ts: Date.now(),
          fields: patch,
        };
        sendMessage(msg);
        prevStateRef.current = { ...prevStateRef.current!, ...patch };
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    state.sessionName, state.backgroundInfo, state.phases,
    state.sessionAvatars, state.requirements, state.facilitatorGuidance,
    state.sessionDevices, state.attendees, state.facilitators, state.sessionStatus,
    state.activeTasks, state.governanceData, state.sessionCreatedAt,
    state.inspirationCards, state.graphData, state.pinnedContent,
    state.doubleDiamondProgress,
    sessionId, nextSeq, sendMessage,
  ]);

  // Flush a queued request_snapshot once state has meaningful data
  useEffect(() => {
    if (!pendingSnapshotRequestRef.current) return;
    const ready = (state.phases?.length ?? 0) > 0 || (state.sessionAvatars?.length ?? 0) > 0;
    if (ready) {
      pendingSnapshotRequestRef.current = false;
      sendSnapshot();
    }
  }, [state.phases, state.sessionAvatars, sendSnapshot]);

  const sendSpotlight = useCallback((contribution: AvatarSuggestion, targetScreen?: number) => {
    sendMessage({
      type: 'spotlight',
      sessionId,
      seq: nextSeq(),
      ts: Date.now(),
      contribution,
      targetScreen,
    });
  }, [sessionId, nextSeq, sendMessage]);

  const clearSpotlight = useCallback((targetScreen?: number) => {
    sendMessage({
      type: 'clear_spotlight',
      sessionId,
      seq: nextSeq(),
      ts: Date.now(),
      targetScreen,
    });
  }, [sessionId, nextSeq, sendMessage]);

  const pinContent = useCallback((item: PinnedContentItem, targetScreen?: number) => {
    sendMessage({
      type: 'pin_content',
      sessionId,
      seq: nextSeq(),
      ts: Date.now(),
      item,
      targetScreen,
    });
  }, [sessionId, nextSeq, sendMessage]);

  const unpinContent = useCallback((itemId: string, targetScreen?: number) => {
    sendMessage({
      type: 'unpin_content',
      sessionId,
      seq: nextSeq(),
      ts: Date.now(),
      itemId,
      targetScreen,
    });
  }, [sessionId, nextSeq, sendMessage]);

  const sendMindBubble = useCallback((avatarRole: string, avatarName: string, thought: string) => {
    const msg: SecondScreenMindBubble = {
      type: 'mind_bubble',
      sessionId,
      seq: nextSeq(),
      ts: Date.now(),
      avatarRole,
      avatarName,
      thought,
    };
    sendMessage(msg);
  }, [sessionId, nextSeq, sendMessage]);

  const sendCinematicCard = useCallback((card: CinematicCard) => {
    const msg: SecondScreenCinematicCard = {
      type: 'cinematic_card',
      sessionId,
      seq: nextSeq(),
      ts: Date.now(),
      card,
    };
    sendMessage(msg);
  }, [sessionId, nextSeq, sendMessage]);

  const sendCinematicContribution = useCallback((contribution: CinematicContribution) => {
    const msg: SecondScreenCinematicContribution = {
      type: 'cinematic_contribution',
      sessionId,
      seq: nextSeq(),
      ts: Date.now(),
      contribution,
    };
    sendMessage(msg);
  }, [sessionId, nextSeq, sendMessage]);

  const sendTaskUpdate = useCallback((tasks: SessionTaskSummary[]) => {
    const msg: SecondScreenTaskUpdate = {
      type: 'task_update',
      sessionId,
      seq: nextSeq(),
      ts: Date.now(),
      tasks,
    };
    sendMessage(msg);
  }, [sessionId, nextSeq, sendMessage]);

  const sendRequirementsFocus = useCallback((activePersonaId: string | null, storiesScrollTop: number) => {
    const msg: SecondScreenRequirementsFocus = {
      type: 'requirements_focus',
      sessionId,
      seq: nextSeq(),
      ts: Date.now(),
      activePersonaId,
      storiesScrollTop,
    };
    sendMessage(msg);
  }, [sessionId, nextSeq, sendMessage]);

  return { sendSpotlight, clearSpotlight, pinContent, unpinContent, sendMindBubble, sendCinematicCard, sendCinematicContribution, sendTaskUpdate, sendRequirementsFocus };
}
